import type ExcelJS from 'exceljs';
import type { AnnualSummaryResponse } from '@app/shared';

const HEADER_FONT = { bold: true } as const;
const TOTAL_FONT = { bold: true } as const;

function addCategoryTable(sheet: ExcelJS.Worksheet, property: AnnualSummaryResponse['properties'][number]): void {
  sheet.addRow(['Catégorie', 'Sens', `Montant (${property.currency})`]).font = HEADER_FONT;
  for (const category of property.categories) {
    sheet.addRow([category.label, category.direction === 'income' ? 'Recette' : 'Dépense', category.totalCents / 100]);
  }
  sheet.addRow([]);
  sheet.addRow(['Recettes totales', '', property.incomeCents / 100]).font = TOTAL_FONT;
  sheet.addRow(['Dépenses totales', '', property.expenseCents / 100]).font = TOTAL_FONT;
  sheet.addRow(['Résultat net', '', property.netCents / 100]).font = TOTAL_FONT;
  sheet.getColumn(1).width = 28;
  sheet.getColumn(2).width = 12;
  sheet.getColumn(3).width = 16;
  sheet.getColumn(3).numFmt = '#,##0.00';
}

/**
 * Un onglet par bien, plus un onglet consolidé — format annuel, fidèle à l'écran Bilan
 * (mêmes chiffres, `AnnualSummaryResponse`, rien de recalculé). Le consolidé additionne les
 * montants sans convertir les devises, exactement comme l'écran : tous les biens du jeu de
 * données sont en EUR à ce stade (voir la limite déjà documentée au lot 9).
 *
 * `exceljs` (~900 Ko) est chargé à la demande : sans ça, son poids entrait dans le bundle
 * précaché par le service worker (lot 5) pour les deux comptes qui n'exportent jamais.
 */
export async function buildBilanWorkbook(summary: AnnualSummaryResponse): Promise<ExcelJS.Workbook> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  for (const property of summary.properties) {
    const sheet = workbook.addWorksheet(property.propertyName.slice(0, 31));
    addCategoryTable(sheet, property);
  }

  const consolidated = workbook.addWorksheet('Consolidé');
  consolidated.addRow(['Bilan annuel consolidé', String(summary.year)]).font = HEADER_FONT;
  consolidated.addRow([]);
  consolidated.addRow(['Recettes totales', summary.consolidated.incomeCents / 100]).font = TOTAL_FONT;
  consolidated.addRow(['Dépenses totales', summary.consolidated.expenseCents / 100]).font = TOTAL_FONT;
  consolidated.addRow(['Résultat net', summary.consolidated.netCents / 100]).font = TOTAL_FONT;
  consolidated.getColumn(1).width = 28;
  consolidated.getColumn(2).width = 16;
  consolidated.getColumn(2).numFmt = '#,##0.00';

  return workbook;
}

export async function downloadWorkbook(fileName: string, workbook: ExcelJS.Workbook): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
